// =====================================================================
// Types partagés entre l'API et la PWA.
// Ils reflètent exactement les types du schéma PostgreSQL.
// =====================================================================

export type Sexe = 'M' | 'F';

export type RoleParent = 'pere' | 'mere';

export type PrecisionDate =
  | 'exacte'
  | 'mois'
  | 'annee'
  | 'approximative'
  | 'inconnue';

/** Ce que l'utilisateur saisit pour désigner une personne. */
export interface Identite {
  nom?: string;
  prenoms?: string;
  sexe?: Sexe;
  date_naissance?: string | null;
  precision_naissance?: PrecisionDate;
  lieu_naissance?: string;
  village_origine?: string;
  territoire?: string;
  province?: string;
  /**
   * Contacts, optionnels. Si l'un des deux correspond à un compte
   * existant, la personne reçoit « X t'a ajouté comme son frère. Le
   * connaissez-vous ? » au lieu de rester un nœud isolé dans l'arbre du
   * déclarant. On accepte les deux parce que les deux usages existent :
   * le numéro d'un frère resté au pays, l'adresse d'un cousin de la
   * diaspora.
   */
  telephone?: string;
  email?: string;
}

/** Une personne de mon arbre, avec la relation calculée par la base. */
export interface Parent {
  personne_id: string;
  nom: string | null;
  prenoms: string | null;
  sexe: Sexe | null;
  village_origine: string | null;
  decede: boolean;
  /** « grand-père », « tante », « cousine germaine », « demi-frère »… */
  libelle: string;
  profondeur_moi: number;
  profondeur_autre: number;
  /** Côté paternel ou maternel par lequel passe la relation. */
  cote: RoleParent | null;
}

/**
 * Une arête du graphe : « parent_id est le père / la mère de enfant_id ».
 * C'est la seule relation réellement stockée ; tout le reste en découle.
 * La RLS ne renvoie que les arêtes de la famille de l'utilisateur.
 */
export interface Lien {
  enfant_id: string;
  parent_id: string;
  role_parent: RoleParent;
}

export interface Moi {
  compte_id: string;
  personne_id: string;
  nom: string | null;
  prenoms: string | null;
  sexe: Sexe | null;
  /** Identifiant de connexion. */
  email: string;
  email_verifie: boolean;
  telephone: string | null;
  /** Faux quand aucun courriel ne peut partir : l'interface ne doit pas
   *  proposer de confirmer son adresse, le bouton serait une impasse. */
  verification_disponible: boolean;
  /** Vrai quand la connexion refuse une adresse non confirmée. */
  verification_exigee: boolean;
}

export type StatutRapprochement =
  | 'propose'
  | 'conversation'
  | 'confirme'
  | 'refuse'
  | 'expire';

/**
 * Un rapprochement proposé par le système.
 * Les champs `nom_autre` et `prenoms_autre` restent nuls tant que les deux
 * parties n'ont pas confirmé : l'identité est masquée pendant l'échange.
 */
export interface Rapprochement {
  id: string;
  statut: StatutRapprochement;
  cree_le: string;
  expire_le: string;
  mon_cote: 'a' | 'b';
  j_ai_confirme: boolean;
  pseudonyme_autre: string;
  nom_autre: string | null;
  prenoms_autre: string | null;
  nom_personne_commune: string | null;
  prenoms_personne_commune: string | null;
  /** 1 = un parent, 2 = un grand-parent. */
  generation: number;
  mes_messages: number;
  messages_recus: number;
  messages_restants: number;
}

export interface Message {
  id: string;
  de_moi: boolean;
  corps: string;
  envoye_le: string;
}

export type RelationDeclaree =
  | 'pere'
  | 'mere'
  | 'enfant'
  | 'frere_soeur_memes_parents'
  | 'frere_soeur_un_parent';

/** « A t'a ajouté comme son frère. Le connaissez-vous ? » */
export interface DemandeLien {
  id: string;
  relation: RelationDeclaree;
  parent_partage: RoleParent | null;
  statut: 'en_attente' | 'confirmee' | 'refusee' | 'expiree';
  cree_le: string;
  expire_le: string;
  /** Nom déclaré par la personne qui m'a ajouté. */
  declarant_nom: string | null;
  declarant_prenoms: string | null;
}

export const LIBELLE_RELATION_DECLAREE: Record<RelationDeclaree, string> = {
  pere: 'son père',
  mere: 'sa mère',
  enfant: 'son enfant',
  frere_soeur_memes_parents: 'son frère ou sa sœur (mêmes parents)',
  frere_soeur_un_parent: 'son demi-frère ou sa demi-sœur',
};

/** Une commande de livre. Le document est un instantané : il n'est pas
 *  régénéré, et `taille` est celle des octets conservés. */
export interface CommandeLivre {
  id: string;
  statut: 'payee' | 'produite' | 'echouee' | 'remboursee';
  personnes: number | null;
  prix_centimes: number;
  cree_le: string;
  taille: number;
}

/** Un lien dont je suis une extrémité : c'est celui-là que je peux
 *  contester. */
export interface MaFiliation {
  enfant_id: string;
  parent_id: string;
  role_parent: RoleParent;
  /** Vrai quand c'est moi l'enfant : « X est déclaré comme mon père ». */
  je_suis_enfant: boolean;
  autre_nom: string | null;
  autre_prenoms: string | null;
  declare_par_moi: boolean;
  cree_le: string;
}

export interface Contestation {
  id: string;
  role_parent: RoleParent;
  je_suis_enfant: boolean;
  autre_nom: string | null;
  autre_prenoms: string | null;
  motif: string | null;
  statut: 'retiree' | 'annulee';
  cree_le: string;
  /** Faux quand un autre lien occupe la place : annuler échouerait. */
  retablissable: boolean;
}

/** Un ancien racontant la lignée. Le son vit sur un volume, chiffré ;
 *  seule cette fiche est en base. */
export interface Recit {
  id: string;
  titre: string | null;
  duree_s: number;
  octets: number;
  type_mime: string;
  cree_le: string;
  /** Le nom de celui qui tenait le micro. */
  depose_par: string | null;
  /** Vrai si je peux l'effacer : je l'ai déposé, ou c'est ma fiche. */
  a_moi: boolean;
}
